import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Plus, Edit, Trash2, Download, Calendar, CheckCircle, XCircle, Clock, FileText as FileContract, Shield } from 'lucide-react';
import http from '../api/http';
import BlueHeader from '../components/BlueHeader';
import { useAuth } from '../context/AuthContext';

const Contrats = () => {
  const { personnelId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [contrats, setContrats] = useState([]);
  const [typesContrat, setTypesContrat] = useState([]);
  const [employe, setEmploye] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedContrat, setSelectedContrat] = useState(null);
  const [formData, setFormData] = useState({
    personnel_id: personnelId,
    type_contrat_id: '',
    type_contrat_mode: 'nouveau',
    date_entree_contrat: '',
    date_fin_contrat: '',
    essai: 0,
    etat: 1,
    verification: 0,
    validation: 0
  });

  const isDirectionGenerale = user?.role === 'super_admin';

  useEffect(() => {
    fetchContrats();
    fetchEmploye();
    fetchTypesContrat();
  }, [personnelId]);

  const fetchContrats = async () => {
    try {
      const response = await http.get(`/api/contrats/${personnelId}`);
      setContrats(response.data);
    } catch (error) {
      console.error('Erreur récupération contrats:', error);
    }
  };

  const fetchEmploye = async () => {
    try {
      const response = await http.get(`/api/personnel/${personnelId}`);
      setEmploye(response.data);
    } catch (error) {
      console.error('Erreur récupération employé:', error);
    }
  };

  const fetchTypesContrat = async () => {
    try {
      const filialeId = localStorage.getItem('filiale_id');
      const response = await http.get(`/api/types-contrat`, {
        params: { filiale_id: filialeId }
      });
      setTypesContrat(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur récupération types contrat:', error);
      setTypesContrat([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddContrat = async (e) => {
    e.preventDefault();
    try {
      await http.post('/api/contrats', formData);
      setShowAddModal(false);
      setFormData({
        personnel_id: personnelId,
        type_contrat_id: '',
        type_contrat_mode: 'nouveau',
        date_entree_contrat: '',
        date_fin_contrat: '',
        essai: 0,
        etat: 1,
        verification: 0,
        validation: 0
      });
      fetchContrats();
    } catch (error) {
      console.error('Erreur ajout contrat:', error);
    }
  };

  const handleEditContrat = async (e) => {
    e.preventDefault();
    try {
      await http.put(`/api/contrats/${selectedContrat.idpersonnel_contrat}`, formData);
      setShowEditModal(false);
      setSelectedContrat(null);
      fetchContrats();
    } catch (error) {
      console.error('Erreur modification contrat:', error);
    }
  };

  const handleDeleteContrat = async (id) => {
    if (!window.confirm('Voulez-vous vraiment supprimer ce contrat ?')) return;
    try {
      await http.delete(`/api/contrats/${id}`);
      fetchContrats();
    } catch (error) {
      console.error('Erreur suppression contrat:', error);
      alert(error.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const handleVerifierContrat = async (id) => {
    if (!window.confirm('Voulez-vous vraiment vérifier ce contrat ?')) return;
    try {
      await http.put(`/api/contrats/${id}/verifier`);
      alert('Contrat vérifié avec succès');
      fetchContrats();
    } catch (error) {
      console.error('Erreur vérification contrat:', error);
      alert('Erreur lors de la vérification');
    }
  };

  const handleValiderContrat = async (id) => {
    if (!window.confirm('Voulez-vous vraiment valider ce contrat ?')) return;
    try {
      await http.put(`/api/contrats/${id}/valider`);
      alert('Contrat validé avec succès');
      fetchContrats();
    } catch (error) {
      console.error('Erreur validation contrat:', error);
      alert('Erreur lors de la validation');
    }
  };

  const openEditModal = (contrat) => {
    setSelectedContrat(contrat);
    setFormData({
      personnel_id: personnelId,
      type_contrat_id: contrat.type_contrat_id,
      type_contrat_mode: contrat.type_contrat_mode || 'nouveau',
      date_entree_contrat: contrat.date_entree_contrat,
      date_fin_contrat: contrat.date_fin_contrat,
      essai: contrat.essai,
      verification: contrat.verification,
      validation: contrat.validation
    });
    setShowEditModal(true);
  };

  const handleExportContrat = async (personnelId) => {
    try {
      const response = await http.get(`/api/documents/contrat/${personnelId}`, {
        responseType: 'blob'
      });
      const url = URL.createObjectURL(response.data);
      Object.assign(document.createElement('a'), {
        href: url,
        download: `contrat_${personnelId}.docx`
      }).click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur export contrat:', error);
      alert('Erreur lors de l\'export du contrat');
    }
  };

  const handleExportRenouvellement = async (personnelId, contrat) => {
    try {
      const response = await http.post(`/api/documents/renouvellement/${personnelId}`, {
        nouvelleFin: contrat.date_fin_contrat,
        dateLettre: new Date().toISOString().split('T')[0]
      }, {
        responseType: 'blob'
      });
      const url = URL.createObjectURL(response.data);
      Object.assign(document.createElement('a'), {
        href: url,
        download: `renouvellement_${personnelId}.docx`
      }).click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur export renouvellement:', error);
      alert('Erreur lors de l\'export de la lettre de renouvellement');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex h-48 items-center justify-center text-sm text-slate-500">
          Chargement…
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <BlueHeader
        title={employe ? `Historique des contrats de ${employe.nom} ${employe.prenom} ${employe.autres_prenom || ''}` : 'Historique des contrats'}
        subtitle="Gestion des contrats de l'employé"
        showBack={true}
        onBack={() => navigate(-1)}
        actions={[
          {
            label: 'Nouveau contrat',
            icon: Plus,
            onClick: () => setShowAddModal(true),
            primary: true
          }
        ]}
      />

      <div className="px-6 py-8">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-slate-100">
          <thead className="bg-sky-50/60">
            <tr>
              <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Type de contrat</th>
              <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Mode</th>
              <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Date d'entrée</th>
              <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Date de fin</th>
              <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Statut</th>
              <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-100">
            {contrats.map((contrat) => (
              <tr key={contrat.idpersonnel_contrat} className="hover:bg-sky-50/50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-slate-900">
                    {contrat.libelle_contrat}
                    {contrat.essai == 1 && <span className="ml-2 text-xs text-orange-600">(PÉRIODE D'ESSAI)</span>}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {contrat.type_contrat_mode === 'renouvellement' ? (
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">Renouvellement</span>
                  ) : (
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Nouveau</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                  {contrat.date_entree_contrat}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                  {contrat.type_contrat_id == 3 && contrat.date_fin_contrat == "0000-00-00" ? "---" : contrat.date_fin_contrat}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex flex-wrap gap-1">
                    {contrat.etat == 1 && (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-sky-100 text-sky-800">Actuel</span>
                    )}
                    {contrat.verification == 0 ? (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">Non vérifié</span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Vérifié</span>
                    )}
                    {contrat.validation == 0 ? (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">Non validé</span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Validé</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex gap-2">
                    {contrat.etat == 1 && contrat.validation != 1 && (
                      <button
                        onClick={() => openEditModal(contrat)}
                        className="text-sky-600 hover:text-sky-800"
                        title="Modifier"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    )}
                    {contrat.validation != 1 && (
                      <button
                        onClick={() => handleDeleteContrat(contrat.idpersonnel_contrat)}
                        className="text-red-600 hover:text-red-800"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                    {isDirectionGenerale && contrat.verification == 0 && (
                      <button
                        onClick={() => handleVerifierContrat(contrat.idpersonnel_contrat)}
                        className="text-purple-600 hover:text-purple-800"
                        title="Vérifier"
                      >
                        <Shield className="w-4 h-4" />
                      </button>
                    )}
                    {isDirectionGenerale && contrat.verification == 1 && contrat.validation == 0 && (
                      <button
                        onClick={() => handleValiderContrat(contrat.idpersonnel_contrat)}
                        className="text-blue-600 hover:text-blue-800"
                        title="Valider"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </button>
                    )}
                    {contrat.validation == 1 && (
                      <>
                        <button
                          onClick={() => handleExportContrat(personnelId)}
                          className="text-yellow-600 hover:text-yellow-800"
                          title="Exporter Contrat"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleExportRenouvellement(personnelId, contrat)}
                          className="text-green-600 hover:text-green-800"
                          title="Exporter Attestation"
                        >
                          <FileText className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal Ajout Contrat */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl border border-slate-200">
            <div className="mb-4 flex items-center justify-between border-b border-slate-200 pb-4">
              <h3 className="text-lg font-bold text-slate-900">Nouveau contrat</h3>
              <button type="button" onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>
            <form onSubmit={handleAddContrat}>
              <div className="space-y-4">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">Type de contrat *</label>
                  <select
                    value={formData.type_contrat_id}
                    onChange={(e) => setFormData({ ...formData, type_contrat_id: e.target.value })}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-sky-500 focus:ring-sky-500"
                    required
                  >
                    <option value="">Sélectionnez le type de contrat</option>
                    {typesContrat.map((type) => (
                      <option key={type.idtype_contrat} value={type.idtype_contrat}>
                        {type.nom}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">Mode de contrat *</label>
                  <select
                    value={formData.type_contrat_mode}
                    onChange={(e) => setFormData({ ...formData, type_contrat_mode: e.target.value })}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-sky-500 focus:ring-sky-500"
                    required
                  >
                    <option value="nouveau">Nouveau contrat</option>
                    <option value="renouvellement">Renouvellement</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">Date de début *</label>
                  <input
                    type="date"
                    value={formData.date_entree_contrat}
                    onChange={(e) => setFormData({ ...formData, date_entree_contrat: e.target.value })}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-sky-500 focus:ring-sky-500"
                    required
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">
                    Date de fin {formData.type_contrat_id != 3 ? '*' : '(optionnel pour CDI)'}
                  </label>
                  <input
                    type="date"
                    value={formData.date_fin_contrat}
                    onChange={(e) => setFormData({ ...formData, date_fin_contrat: e.target.value })}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-sky-500 focus:ring-sky-500"
                    required={formData.type_contrat_id != 3}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">Période d'essai</label>
                  <select
                    value={formData.essai}
                    onChange={(e) => setFormData({ ...formData, essai: parseInt(e.target.value) })}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-sky-500 focus:ring-sky-500"
                  >
                    <option value="0">NON</option>
                    <option value="1">OUI</option>
                  </select>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3 border-t border-slate-200 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700"
                >
                  Ajouter
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Modification Contrat */}
      {showEditModal && selectedContrat && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl border border-slate-200">
            <div className="mb-4 flex items-center justify-between border-b border-slate-200 pb-4">
              <h3 className="text-lg font-bold text-slate-900">Modifier le contrat</h3>
              <button type="button" onClick={() => { setShowEditModal(false); setSelectedContrat(null); }} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>
            <form onSubmit={handleEditContrat}>
              <div className="space-y-4">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">Type de contrat *</label>
                  <select
                    value={formData.type_contrat_id}
                    onChange={(e) => setFormData({ ...formData, type_contrat_id: e.target.value })}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-sky-500 focus:ring-sky-500"
                    required
                  >
                    <option value="">Sélectionnez le type de contrat</option>
                    {typesContrat.map((type) => (
                      <option key={type.idtype_contrat} value={type.idtype_contrat}>
                        {type.nom}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">Mode de contrat *</label>
                  <select
                    value={formData.type_contrat_mode}
                    onChange={(e) => setFormData({ ...formData, type_contrat_mode: e.target.value })}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-sky-500 focus:ring-sky-500"
                    required
                  >
                    <option value="nouveau">Nouveau contrat</option>
                    <option value="renouvellement">Renouvellement</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">Date de début *</label>
                  <input
                    type="date"
                    value={formData.date_entree_contrat}
                    onChange={(e) => setFormData({ ...formData, date_entree_contrat: e.target.value })}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-sky-500 focus:ring-sky-500"
                    required
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">
                    Date de fin {formData.type_contrat_id != 3 ? '*' : '(optionnel pour CDI)'}
                  </label>
                  <input
                    type="date"
                    value={formData.date_fin_contrat}
                    onChange={(e) => setFormData({ ...formData, date_fin_contrat: e.target.value })}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-sky-500 focus:ring-sky-500"
                    required={formData.type_contrat_id != 3}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">Période d'essai</label>
                  <select
                    value={formData.essai}
                    onChange={(e) => setFormData({ ...formData, essai: parseInt(e.target.value) })}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-sky-500 focus:ring-sky-500"
                  >
                    <option value="0">NON</option>
                    <option value="1">OUI</option>
                  </select>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3 border-t border-slate-200 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedContrat(null);
                  }}
                  className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700"
                >
                  Modifier
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Contrats;
